<template>
    <div v-if="showPayoutsTab">
        <div v-if="!featureFlags['live-site-responsive-design']" class="box">
            <a
                v-if="isPaymentRails && !showOnlyConversionsAndPayments"
                class="mr-10"
                @click.prevent="showSection(payoutsRef)"
                >Payouts</a
            >
            <a class="mr-10" @click.prevent="showSection(payoutActivityRef)">Payout Activity</a>
            <a
                v-if="!isPaymentRails && payoutAccountCompleted && !showOnlyConversionsAndPayments"
                class="mr-10"
                @click.prevent="showSection(payoutMethodsRef)"
                >Payout Methods
            </a>
            <a
                v-if="payoutAccount.data && !isPaymentRails && !showOnlyConversionsAndPayments"
                class="mr-10"
                @click.prevent="showSection(taxInformationRef)"
                >Tax Info
            </a>
        </div>

        <div class="box">
            <div v-if="!isPaidToAgent && !isCreatorLoading">
                <g-typography tag="h1" font-weight="semibold" value="Payouts" />
                <g-typography class="g-mt-3" tag="p" color="grey-6" value="View your payment related transactions." />
            </div>

            <div v-if="!showOnlyConversionsAndPayments">
                <OnboardingBanner
                    :visible="!isPaymentRails && !payoutMethodAdded"
                    :is-loading="payoutAccount.loading"
                    :payout-account-completed="payoutAccountCompleted"
                    @open-country-selection="handleOpenCountrySelection"
                    @open-onboarding-drawer="handleOpenOnboardingDrawer"
                    @redirect-to-stripe-connect="handleCreateOnboardingLink"
                />

                <div v-if="payoutAccountCompleted" ref="payoutMethodsRef">
                    <g-divider margin="large" />
                    <g-typography tag="h4" font-weight="semibold">Payout Methods</g-typography>

                    <g-typography class="g-mt-3" tag="p" color="grey-6">
                        Securely add and manage your payout methods
                    </g-typography>

                    <PayoutMethods
                        :visible="payoutAccountCompleted"
                        :is-loading="payoutAccount.loading"
                        :is-link-loading="isLinkLoading"
                        :payout-account="payoutAccount"
                        :force-open-add-payout-method-drawer="forceOpenAddPayoutMethodDrawer"
                        @refresh-payout-account="handleRefreshPayoutAccount"
                    />
                </div>

                <div ref="payoutsRef">
                    <div v-if="isPaymentRails">
                        <g-divider margin="large" />
                        <div class="columns">
                            <div class="column is-12">
                                <g-loader v-if="payoutAccount.loading" />
                                <iframe
                                    v-else
                                    ref="iframeRef"
                                    :src="payoutAccount.data.external_account.widget_url"
                                    width="100%"
                                    id="trolley-payouts-iframe"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <g-divider margin="large" />
            </div>

            <div ref="payoutActivityRef">
                <g-typography tag="h4" font-weight="semibold">Payout Activity</g-typography>
                <g-typography class="g-mt-3" tag="p" color="grey-6"
                    >View and track your payments and conversions</g-typography
                >
                <payments-and-conversions />
            </div>

            <div
                v-if="payoutAccount.data && !isPaymentRails && !showOnlyConversionsAndPayments"
                ref="taxInformationRef"
            >
                <g-divider margin="large" />
                <g-typography tag="h4" font-weight="semibold">Tax Information</g-typography>

                <g-typography class="g-mt-3" tag="p" color="grey-6">
                    Keep your business details and tax information up to date
                </g-typography>

                <TaxInformation
                    :is-loading="payoutAccount.loading"
                    :is-link-loading="isLinkLoading"
                    :payout-account="payoutAccount.data"
                    :onboarding-button-label="onboardingButtonLabel()"
                    :onboarding-button-type="onboardingButtonType()"
                    @redirect-to-stripe-connect="handleRedirectToStripeConnect"
                />
            </div>

            <referral-banner />

            <CountrySelection
                :visible="isCountrySelectionVisible"
                @navigate-to="handleNavigateTo"
                @close="hideCountrySelection()"
                @open-onboarding-drawer="handleOpenOnboardingDrawer"
                @open-trolley-onboarding-drawer="handleOpenTrolleyOnboardingDrawer"
            />

            <OnboardingStepsDrawer
                :visible="isOnboardingDrawerVisible"
                :is-loading="payoutAccount.loading"
                :is-link-loading="isLinkLoading"
                :payout-account="payoutAccount.data"
                :step="currentOnboardingStep"
                @redirect-to-stripe-connect="handleCreateOnboardingLink"
                @open-country-selection="handleOpenCountrySelection"
                @open-add-payout-method-drawer="handleOpenAddPayoutMethodDrawer"
                @close="hideOnboardingDrawer()"
            />

            <TrolleyOnboardingStepsDrawer
                :visible="isTrolleyOnboardingDrawerVisible"
                @close="hideTrolleyOnboardingDrawer()"
            />

            <OnboardingPollDrawer
                :visible="isOnboardingPollDrawerVisible"
                @refresh-payout-account="handleRefreshPayoutAccount"
            />
        </div>
    </div>
</template>

<script>
    import { ref, computed, onMounted, onBeforeMount } from 'vue-demi';
    import { get } from 'lodash';
    import PayoutMethods from './partials/PayoutMethods.vue';
    import TaxInformation from './partials/TaxInformation.vue';
    import {
        PAYOUT_PROVIDER_PAYMENT_RAILS,
        STEP_TAX_INFO_ID,
        GRIN_CARD_STATUS_ACTIVE,
        GRIN_CARD_STATUS_CANCELED,
        GRIN_CARD_ACTIVATION_SUCCESS_MESSAGE,
        GRIN_CARD_CANCELATION_SUCCESS_MESSAGE,
        PAYOUT_ACCOUNT_SUSPENDED_TEXT,
        PAYOUT_ACCOUNT_UPDATE_REQUIRED_TEXT,
        PAYOUT_ACCOUNT_STATUS_ACTIVE
    } from '../../../../app-v2/constants/payouts';
    import {
        EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_COMPLETED,
        EVENT_NAME_PAYOUTS_STRIPE_KYC_VIEWED,
        EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_VIEWED
    } from '../../../../app-v2/constants/analytics-events';
    import OnboardingBanner from './onboarding/OnboardingBanner.vue';
    import OnboardingStepsDrawer from './onboarding/OnboardingStepsDrawer.vue';
    import TrolleyOnboardingStepsDrawer from './onboarding/TrolleyOnboardingStepsDrawer.vue';
    import OnboardingPollDrawer from './onboarding/OnboardingPollDrawer.vue';
    import CountrySelection from './onboarding/CountrySelection.vue';
    import useOnboarding from '../../../composables/use-onboarding';
    import useToggle from '../../../composables/use-toggle';
    import useMessage from '../../../composables/use-message';
    import useEventTracking from '../../../composables/use-event-tracking';
    import useRouter from '../../../composables/use-router';
    import {
        useContactStore,
        usePayoutAccountStore,
        useFeatureFlagsStore,
        useAuthStore,
        useCreatorStore
    } from '../../../../stores';
    import ReferralBanner from './referral/ReferralBanner.vue';
    import PaymentsAndConversions from './partials/PaymentsAndConversions.vue';

    export default {
        /**
         * The component name
         *
         * @type {string}
         */
        name: 'PaymentDashboard',

        /**
         * The child components
         *
         * @type {Object}
         */
        components: {
            PaymentsAndConversions,
            TrolleyOnboardingStepsDrawer,
            OnboardingPollDrawer,
            PayoutMethods,
            OnboardingBanner,
            OnboardingStepsDrawer,
            CountrySelection,
            TaxInformation,
            ReferralBanner
        },

        setup(props, context) {
            const contactStore = useContactStore();
            const contact = contactStore.data;
            const creatorStore = useCreatorStore();
            const payoutAccount = usePayoutAccountStore();
            const featureFlags = useFeatureFlagsStore().data;
            const { message } = useMessage();
            const events = useEventTracking();
            const router = useRouter().get();

            /*
             * Element refs
             */
            const payoutsRef = ref(null);
            const payoutActivityRef = ref(null);
            const payoutMethodsRef = ref(null);
            const taxInformationRef = ref(null);
            const iframeRef = ref(null);

            /*
             * Agent payout logic.
             */
            const isPaidToAgent = computed(() => {
                return creatorStore.data?.creator?.is_paid_to_agent;
            });
            const isCreatorLoading = computed(() => {
                return creatorStore.isLoading;
            });
            creatorStore.getCreator();

            /*
             * Payout Account logic.
             */
            const isPaymentRails = computed(() => {
                return payoutAccount.data && payoutAccount.data?.provider === PAYOUT_PROVIDER_PAYMENT_RAILS;
            });
            const payoutAccountSuspended = computed(() => {
                return payoutAccount.data && payoutAccount.data?.external_account?.suspended === true;
            });
            const payoutAccountUpdateRequired = computed(() => {
                return payoutAccount.data && payoutAccount.data?.external_account?.completed_onboarding === false;
            });
            const payoutAccountCompleted = computed(() => {
                return payoutAccount.data && payoutAccount.data?.external_account?.completed_onboarding;
            });
            const payoutMethodAdded = computed(() => {
                return !!(
                    payoutAccount.data?.external_account?.payout_methods?.length > 0 || payoutAccount.data?.payout_card
                );
            });

            const showSection = (ref) => {
                const top = ref.offsetTop;

                window.scrollTo({
                    top,
                    behavior: 'smooth'
                });
            };

            const showPayoutsTab = computed(() => {
                return (
                    (context.root.account_makes_payments || context.root.account_show_payouts_on_live_url) &&
                    useAuthStore().isLoggedIn &&
                    !!contact.last_verified_email
                );
            });

            const showOnlyConversionsAndPayments = computed(() => {
                return (
                    isCreatorLoading.value ||
                    isPaidToAgent.value ||
                    (!context.root.account_makes_payments && context.root.account_show_payouts_on_live_url)
                );
            });

            let checkCardStatus = null;
            const handleRefreshPayoutAccount = (checkStatus = null) => {
                if (checkStatus) {
                    checkCardStatus = checkStatus;
                }

                payoutAccount.load();
            };

            /*
             * Onboarding logic.
             */
            const {
                buttonLabel: onboardingLinkLabel,
                buttonType: onboardingLinkType,
                hasStarted: hasStartedOnboarding,
                createOnboardingLink,
                checkOnboardingSuccess
            } = useOnboarding();
            const { currentValue: isLinkLoading, setTrue: linkLoading, setFalse: linkDoneLoading } = useToggle(false);

            const handleCreateOnboardingLink = async () => {
                linkLoading();
                try {
                    const onboardingLinkRes = await createOnboardingLink(contact.id, payoutAccountCompleted);
                    const onboardingLink = onboardingLinkRes?.data?.url;
                    if (!onboardingLink) {
                        message.error('Oops! Could not retrieve a link for onboarding your payment account.');
                        events.trackEvent(EVENT_NAME_PAYOUTS_STRIPE_KYC_VIEWED, getKYCEventPayload(), false);
                        return;
                    }
                    events.trackEvent(EVENT_NAME_PAYOUTS_STRIPE_KYC_VIEWED, getKYCEventPayload());
                    window.location.href = onboardingLink;
                    setTimeout(() => {
                        linkDoneLoading();
                    }, 5000);
                } catch (e) {
                    message.error(`Oops! Something went wrong: ${message.responseError(e)}`);
                    events.trackEvent(EVENT_NAME_PAYOUTS_STRIPE_KYC_VIEWED, getKYCEventPayload(), false);
                    linkDoneLoading();
                }
            };

            const getKYCEventPayload = () => {
                return {
                    contact_id: contact.id
                };
            };

            const handleRedirectToStripeConnect = async () => {
                if (!hasStartedOnboarding()) {
                    handleOpenCountrySelection();
                    return;
                }

                await handleCreateOnboardingLink();
            };

            const onboardingButtonLabel = () => {
                return onboardingLinkLabel(hasStartedOnboarding(), payoutAccountCompleted.value);
            };

            const onboardingButtonType = () => {
                return onboardingLinkType(hasStartedOnboarding(), payoutAccountCompleted.value);
            };

            /**
             * Onboarding drawer
             */
            const {
                currentValue: isOnboardingDrawerVisible,
                setTrue: showOnboardingDrawer,
                setFalse: hideOnboardingDrawer
            } = useToggle(false);

            const currentOnboardingStep = ref(STEP_TAX_INFO_ID);
            const drawerWaitingOnRefresh = ref(false);

            const handleOpenOnboardingDrawer = (step, refresh = false) => {
                currentOnboardingStep.value = step;

                if (refresh) {
                    handleRefreshPayoutAccount();
                    drawerWaitingOnRefresh.value = true;
                } else {
                    hideCountrySelection();
                    showOnboardingDrawer();
                }
            };

            /**
             * Trolley onboarding drawer
             */
            const {
                currentValue: isTrolleyOnboardingDrawerVisible,
                setTrue: showTrolleyOnboardingDrawer,
                setFalse: hideTrolleyOnboardingDrawer
            } = useToggle(false);

            const handleOpenTrolleyOnboardingDrawer = (refresh = false) => {
                if (refresh) {
                    handleRefreshPayoutAccount();
                    drawerWaitingOnRefresh.value = true;
                } else {
                    hideCountrySelection();
                    showTrolleyOnboardingDrawer();
                }
            };

            /**
             * Onboarding Poll Drawer
             */
            const { currentValue: isOnboardingPollDrawerVisible, setTrue: showOnboardingPollDrawer } = useToggle(false);

            /**
             * Add Payout Method Drawer
             */
            const forceOpenAddPayoutMethodDrawer = ref(false);
            const handleOpenAddPayoutMethodDrawer = () => {
                forceOpenAddPayoutMethodDrawer.value = true;
                setTimeout(() => {
                    forceOpenAddPayoutMethodDrawer.value = false;
                }, 500);
            };

            /*
             * Country Selection logic
             */
            const {
                currentValue: isCountrySelectionVisible,
                setTrue: showCountrySelection,
                setFalse: hideCountrySelection
            } = useToggle(false);

            const handleNavigateTo = (url) => {
                window.location.href = url;
            };

            const handleOpenCountrySelection = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_COUNTRY_SELECTOR_VIEWED, { contact_id: contact.id });
                showCountrySelection();
            };

            /**
             * Lifecycle methods
             */
            onBeforeMount(() => {
                payoutAccount.load();
            });

            onMounted(() => {
                if (!showPayoutsTab.value) {
                    router.push({ name: 'edit.profile' });
                }

                checkOnboardingSuccess(contact.id);

                window.addEventListener('message', (e) => {
                    const data = get(e, 'data');
                    if (data.event === 'document.height' && iframeRef.value) {
                        iframeRef.value.style.height = `${data.document.height}px`;
                    }
                });

                payoutAccount.$subscribe((mutation, state) => {
                    if (!state.loading) {
                        /**
                         * Waits after creating their payout account on the country selection drawer
                         * for the payout account to fully reload before showing them the next
                         * onboarding step so they don't see a loader from the account creation
                         * only to be redirected to another loader on the onboarding steps
                         */
                        if (drawerWaitingOnRefresh.value) {
                            drawerWaitingOnRefresh.value = false;
                            hideCountrySelection();
                            if (isPaymentRails.value) {
                                showTrolleyOnboardingDrawer();
                            } else {
                                showOnboardingDrawer();
                            }
                        }

                        if (checkCardStatus) {
                            if (checkCardStatus === GRIN_CARD_STATUS_ACTIVE) {
                                let successful = false;
                                if (state.data?.payout_card?.status === GRIN_CARD_STATUS_ACTIVE) {
                                    message.success(GRIN_CARD_ACTIVATION_SUCCESS_MESSAGE);
                                    successful = true;
                                }

                                events.trackEvent(
                                    EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_COMPLETED,
                                    {
                                        contact_id: contact.id
                                    },
                                    successful
                                );
                            } else if (
                                checkCardStatus === GRIN_CARD_STATUS_CANCELED &&
                                state.data?.payout_card?.status === GRIN_CARD_STATUS_CANCELED
                            ) {
                                message.success(GRIN_CARD_CANCELATION_SUCCESS_MESSAGE);
                            }

                            checkCardStatus = null;
                        }

                        //check state for onboarding poll drawer
                        if (
                            state.data?.status === PAYOUT_ACCOUNT_STATUS_ACTIVE &&
                            !state.data?.onboarding_poll_viewed
                        ) {
                            showOnboardingPollDrawer();
                        }
                    }
                });
            });

            return {
                //methods
                showSection,
                showPayoutsTab,
                showOnlyConversionsAndPayments,

                //element refs
                payoutsRef,
                payoutActivityRef,
                payoutMethodsRef,
                taxInformationRef,
                iframeRef,

                // Agent payout
                isPaidToAgent,
                isCreatorLoading,

                // Payout Account
                payoutAccount,
                isPaymentRails,
                payoutAccountSuspended,
                payoutAccountUpdateRequired,
                payoutAccountCompleted,
                handleRefreshPayoutAccount,
                payoutMethodAdded,

                // Onboarding
                createOnboardingLink,
                handleCreateOnboardingLink,
                handleRedirectToStripeConnect,
                isLinkLoading,
                onboardingButtonLabel,
                onboardingButtonType,

                // Onboarding drawer
                isOnboardingDrawerVisible,
                hideOnboardingDrawer,
                currentOnboardingStep,

                // Trolley onboarding drawer
                isTrolleyOnboardingDrawerVisible,
                hideTrolleyOnboardingDrawer,

                // Onboarding drawer
                isOnboardingPollDrawerVisible,

                // Add payout method drawer
                handleOpenAddPayoutMethodDrawer,
                forceOpenAddPayoutMethodDrawer,

                // Country Selection
                isCountrySelectionVisible,
                showCountrySelection,
                hideCountrySelection,
                handleNavigateTo,
                handleOpenOnboardingDrawer,
                handleOpenTrolleyOnboardingDrawer,
                handleOpenCountrySelection,

                // text consts
                PAYOUT_ACCOUNT_SUSPENDED_TEXT,
                PAYOUT_ACCOUNT_UPDATE_REQUIRED_TEXT,

                featureFlags
            };
        }
    };
</script>
